import { useEffect, useState } from "react";
import { api, fmtEur, BACKEND_URL, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FilePdf, PaperPlaneTilt, Receipt, Wallet, CheckCircle, Warning } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function nowMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * MyMonthlyInvoice — el colaborador genera (y envía al titular) su factura
 * mensual con el total que cobra por sus servicios realizados.
 * Backend:
 *   GET /api/payroll/me?month=YYYY-MM       → resumen (preview)
 *   GET /api/payroll/me/pdf?month=YYYY-MM   → descarga PDF de su liquidación
 *   POST /api/payroll/me/generate-invoice?month=YYYY-MM → emite y la inserta
 *        automáticamente como GASTO del titular (con el PDF adjunto).
 */
export default function MyMonthlyInvoice() {
  const { user } = useAuth();
  const [month, setMonth] = useState(nowMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    if (!month) return;
    setBusy(true);
    try { const { data } = await api.get(`/payroll/me?month=${month}`); setData(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const downloadPdf = () => {
    fetch(`${BACKEND_URL}/api/payroll/me/pdf?month=${month}`, {
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `mi-liquidacion-${month}.pdf`;
        a.click();
      })
      .catch(() => toast.error("No se pudo descargar"));
  };

  const submitToTitular = async () => {
    const missing = [];
    if (!user?.billing_nif) missing.push("NIF/DNI");
    if (!user?.bank_iban) missing.push("IBAN");
    if (missing.length) {
      toast.error(`Configura tus datos fiscales en Ajustes (${missing.join(", ")})`);
      return;
    }
    if (!window.confirm(`¿Confirmar emisión de tu factura mensual ${month} al titular? Quedará registrada automáticamente en sus Gastos con el PDF adjunto.`)) return;
    setGenerating(true);
    try {
      const { data: r } = await api.post(`/payroll/me/generate-invoice?month=${month}`);
      toast.success(`Factura enviada al titular · ${fmtEur(r.totals.neto)}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const incompleteFiscal = !user?.billing_nif || !user?.bank_iban;

  return (
    <div className="space-y-6 max-w-4xl">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Auto-facturación</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">Mi factura mensual</h1>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2">Genera tu factura del mes con el <b>total que tienes que cobrar</b> por los servicios realizados. El titular la recibirá automáticamente en sus Gastos.</p>
      </header>

      {incompleteFiscal && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4 flex gap-3 items-start" data-testid="incomplete-fiscal-warning">
          <Warning size={20} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <b className="text-amber-300">Te faltan datos fiscales</b>
            <div className="text-[var(--ag-text-dim)] mt-1">Completa tu NIF/DNI e IBAN en <Link to="/settings" className="text-[var(--ag-gold)] underline">Ajustes</Link> para poder emitir la factura.</div>
          </div>
        </div>
      )}

      <div className="ag-card p-5 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div>
          <Label className="text-xs uppercase tracking-wider">Mes a facturar</Label>
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="mt-1 rounded-sm" data-testid="invoice-month" />
        </div>
        <Button onClick={downloadPdf} disabled={busy || !data || data.totals.count === 0} variant="outline" className="h-11 rounded-sm" data-testid="download-my-pdf">
          <FilePdf className="mr-1.5" /> Descargar PDF
        </Button>
        <Button onClick={submitToTitular} disabled={generating || incompleteFiscal || !data || data.totals.count === 0}
          className="ag-btn-gold h-11" data-testid="submit-to-titular">
          <PaperPlaneTilt className="mr-1.5" weight="fill" /> {generating ? "Enviando…" : "Emitir factura al titular"}
        </Button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="metric-card p-5"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Servicios</div><div className="font-display font-bold text-3xl tabular-nums mt-1">{data.totals.count}</div></div>
          <div className="metric-card p-5"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Bruto (base)</div><div className="font-display font-bold text-3xl tabular-nums mt-1">{fmtEur(data.totals.bruto)}</div></div>
          <div className="metric-card p-5"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">+ IVA</div><div className="font-display font-bold text-3xl tabular-nums mt-1">{fmtEur(data.totals.iva)}</div></div>
          <div className="metric-card p-5 bg-[#111827] text-white border-[#111827]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Líquido a cobrar</div>
            <div className="font-display font-bold text-3xl tabular-nums mt-1 text-emerald-400">{fmtEur(data.totals.neto)}</div>
            <div className="text-[10px] text-white/60 mt-1">-Retención {fmtEur(data.totals.retencion)}</div>
          </div>
        </div>
      )}

      {data && data.totals.count === 0 && (
        <div className="ag-card p-8 text-center text-[var(--ag-text-dim)]" data-testid="empty-month">
          <Receipt size={40} weight="duotone" className="text-[var(--ag-text-dim)] mx-auto mb-3" />
          <div className="font-display text-xl">Sin servicios este mes</div>
          <div className="text-sm mt-2">No hay nada que facturar todavía. Cuando realices servicios, podrás generar tu factura aquí.</div>
        </div>
      )}

      <div className="bg-[var(--ag-aqua)]/5 border border-[var(--ag-aqua)]/20 rounded-sm p-4 text-sm">
        <div className="flex items-center gap-2 text-[var(--ag-aqua-soft)] font-semibold mb-2"><Wallet size={14} weight="fill" /> ¿Cómo funciona?</div>
        <ol className="space-y-1.5 list-decimal pl-5 text-[var(--ag-text-dim)]">
          <li>El sistema calcula tu factura: base × {Math.round((data?.colaborador?.retention_irpf_rate || 15))}% retención IRPF + IVA → líquido a cobrar.</li>
          <li>Genera tu PDF y revísalo (descarga).</li>
          <li>Pulsa "Emitir factura al titular" — el PDF se adjuntará automáticamente a un Gasto en su panel.</li>
          <li>El titular hará la transferencia al IBAN que tienes en Ajustes.</li>
        </ol>
      </div>
    </div>
  );
}
